import Brand from "@modules/home/brand";
import Carousel from "@modules/home/carousel";
import Categories from "@modules/home/categories";
import Widget from "@modules/home/widget";
import CollectionList from "@modules/home/collectionList";
import { IHomeDataInput } from "@types";
import { useAppContext } from "@context/app";
import { useEffect, useState } from "react";
import VideoList from "@modules/home/videoList";
import dynamic from "next/dynamic";
import HomeDonationBanner from "@modules/donation/HomeDonationBanner";
import GiftSenderDrawer from "@modules/gift/GiftSenderDrawer";
import { useRouter } from "next/router";
import MainBanner from "@modules/home/mainBanner";
const ActiveOrder = dynamic(() => import("@modules/home/activeOrder"), { ssr: false });
const GiftListDrawer = dynamic(() => import("@modules/gift/GiftRecipientDrawer").catch(() => () => null), { ssr: false });

interface Props {
  data?: IHomeDataInput;
}

const Home: React.FC<Props> = ({ data }) => {
  const { setEventProperties, user, syncData } = useAppContext();
  const router = useRouter();

  // Check if send_gift parameter exists in the URL
  const hasSendGiftParam = !!router.query.send_gift;

  // Set initial drawer state based on send_gift parameter
  const [isGiftDrawerOpen, setIsGiftDrawerOpen] = useState(hasSendGiftParam);
  const [isGiftListDrawerOpen, setIsGiftListDrawerOpen] = useState(false);

  useEffect(() => {
    setEventProperties({
      from_page: "homepage",
      from_entity: "home",
      from_entity_slug: data?.entity?.slug || "",
      from_page_section: "",
      from_page_section_entity: "",
      from_page_section_slug: "",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update drawer state when URL changes
  useEffect(() => {
    setIsGiftDrawerOpen(!!router.query.send_gift);
  }, [router.query.send_gift]);

  // Watch for specific hash fragments that come from linkGenerator
  useEffect(() => {
    const handleAppLinks = () => {
      // Check for hash fragments from linkGenerator
      const currentPath = router.asPath;

      // Handle #openSendDigitalGift hash fragment
      if (currentPath.includes("#openSendDigitalGift")) {
        setIsGiftDrawerOpen(true);

        // Remove the hash fragment to prevent re-triggering on refresh
        if (typeof window !== 'undefined') {
          const newUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, newUrl);
        }
      }

      // Handle #openListDigitalGift hash fragment
      if (currentPath.includes("#openListDigitalGift")) {
        setIsGiftListDrawerOpen(true);

        // Remove the hash fragment to prevent re-triggering on refresh
        if (typeof window !== 'undefined') {
          const newUrl = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, newUrl);
        }
      }

      // Legacy handling for URL parameters
      if (router.query.send_gift) {
        setIsGiftDrawerOpen(true);
      }
    };

    handleAppLinks();

    // Listen for route changes to handle new hash fragments
    router.events.on("routeChangeComplete", handleAppLinks);

    // Also handle hash changes that might not trigger a full route change
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', handleAppLinks);
    }

    return () => {
      router.events.off("routeChangeComplete", handleAppLinks);
      if (typeof window !== 'undefined') {
        window.removeEventListener('hashchange', handleAppLinks);
      }
    };
  }, [router]);

  return (
    <div className="w-full max-w-[1256px] px-2 mx-auto">
      <GiftSenderDrawer
        isOpen={isGiftDrawerOpen}
        onClose={() => setIsGiftDrawerOpen(false)}
      />
      {/* Conditionally render the GiftListDrawer if it exists */}
      {GiftListDrawer && (
        <GiftListDrawer
          isOpen={isGiftListDrawerOpen}
          onClose={() => setIsGiftListDrawerOpen(false)}
          digital_gifts={user.data?.digital_gifts || []}
        />
      )}
      {syncData?.allow_donation &&
        <div className="px-3 my-4 sm:mb-10 xs:mb-5 mb-5">
          <HomeDonationBanner donations_count={syncData?.donations_count} />
        </div>
      }
      {user.isLoggedIn && syncData?.app_settings.active_order_tracker ? <ActiveOrder /> : null}
      {data?.items.map((item) => (
        <div
          key={`home-${item.e?.v ?? item.items[0]?.id}`}
          className="sm:mb-10 xs:mb-5 mb-5"
        >
          {item.type === "slider" && (
            <div className="mt-10 mb-16">
              <Carousel data={item} sourceEntity={data.entity} />
            </div>
          )}
          {item.type === "video_sku" && (
            <div>
              <VideoList data={item} sourceEntity={data.entity} />
            </div>
          )}
          {item.type == "category" && (
            <div >
              <Categories data={item} sectionEntity={data.entity} />
            </div>
          )}
          {item.type === "product" && (
            <div>
              <CollectionList data={item} sourceEntity={data.entity} />
            </div>
          )}
          {item.type === "widget" && (
            <div>
              <Widget data={item} sourceEntity={data.entity} />
            </div>
          )}
          {item.type === "brand" && (
            <div>
              <Brand data={item} sectionEntity={data.entity} />
            </div>
          )}
          {item.type === "mainBanner" && (
            <div>
              <MainBanner
                data={item}
                sourceEntity={data.entity}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Home;
